import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NestedPropertyPipe } from '@zaoblako/web/shared/pipes/nested-property.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { MultiSelect, MultiSelectModule } from 'primeng/multiselect';

@Component({
  standalone: true,
  selector: 'crm-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  imports: [CommonModule, MultiSelectModule, OrderByPipe, FormsModule, NestedPropertyPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiSelectComponent implements OnInit, OnChanges {
  @ViewChild('multiSelect') public multiSelect: MultiSelect;
  public isNotNamedArray = false;

  @Input() public options: any[];
  @Input() public selectedItems: any[];
  @Output() public selectedItemsChange = new EventEmitter<any>();
  @Input() public dataKey: string = 'id';
  @Input() public placeholder = '\u00A0';
  @Input() public inputId = `${Math.random()}`;
  @Input() public label: string;
  @Input() public itemDescriptionField: string;
  @Input() public itemSubDescriptionField: string;
  @Input() public maxSelectedLabels = 2;
  @Input() public dropdownIcon = 'pi pi-chevron-down';
  @Input() public showHeader: boolean = true;
  @Input() public panelStyleClass: string;
  @Input() public optionLabel = 'name';
  @Input() public sort = this.optionLabel;
  @Input() public optionPrefix = null;
  @Input() public disabled = false;
  @Input() public group = false;
  @Input() public filterBy = 'name';
  @Input() public emptyFilterMessage = 'Нет результатов';
  @Input() public emptyMessage = 'Список пуст';
  @Input() public optionDisabled = 'inactive';
  @Input() public selectedItemsLabel = 'Выбрано: {0}';
  @Input() public selectedItemsOptions = { updateOn: 'default' };
  @Input() public appendTo: any;
  @Input() public scrollHeight = '300px';
  @Input() public display = 'chip';
  @Input() public showToggleAll = false;
  @Input() public lazy = false;
  @Input() public moveDown = false;
  @Input() public showClear = true;
  @Input() public isFilterSelectedItems = false;

  @Output() public changeValue = new EventEmitter<any>();
  @Output() public panelHide = new EventEmitter<any>();
  @Output() public panelShow = new EventEmitter<any>();
  @Output() public openedChange = new EventEmitter<boolean>();
  @Output() public onLazyLoad = new EventEmitter<{ first: number; last: number }>();
  @Output() public onFilter = new EventEmitter<{ originalEvent: InputEvent; filter: string }>();

  @Input() public isSendFromChange = false;
  @Output() public onFinalChange = new EventEmitter<any>();
  private readonly TIMER_FULL_CHANGE = 700;
  private timerFromChange: any;

  @Input() public class = '';
  @Input() public styleClass = '';
  @Input() public panelStyle: any;

  @Input() public virtualScroll = false;
  @Input() public virtualScrollItemSize = 40;

  @ContentChild('itemTemplate', { static: false }) public itemTemplate;
  @ContentChild('selectedItemsTemplate', { static: false }) public selectedItemsTemplate;
  @ContentChild('footerTemplate', { static: false }) public footerTemplate;

  @Input() public isTemplateCombined = false;

  public replaceRegExp = /{[0-9]+}/g;
  private isDisplayChip = false;

  public get multiSelectDisplay(): string {
    if (this.isDisplayChip) {
      return this.selectedItems?.length > this.maxSelectedLabels ? 'comma' : 'chip';
    } else {
      return this.display;
    }
  }

  public constructor(private readonly renderer2: Renderer2) {}

  public ngOnInit() {
    if (this.display === 'chip') {
      this.isDisplayChip = true;
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.options && Array.isArray(changes.options.currentValue)) {
      this.setNamedValue(changes.options.currentValue, 'options');
    }

    if (changes.selectedItems && Array.isArray(changes.selectedItems.currentValue)) {
      this.setNamedValue(changes.selectedItems.currentValue, 'selectedItems');
    }

    if (changes.display?.currentValue === '') {
      this.showToggleAll = true;
    }
  }

  public setNamedValue(items: string[] | number[], fieldToRewrite: 'options' | 'selectedItems') {
    if (typeof items[0] === 'string' || typeof items[0] === 'number') {
      this[fieldToRewrite] = (items as any[])
        .filter((item: any, index: number) => (items as any[]).indexOf(item) === index)
        .map((item: any) => ({
          name: item,
          value: item
        }));
      this.isNotNamedArray = true;
    } else {
      this[fieldToRewrite] = (items as any[]).filter(
        (item: any, index: number) =>
          (items as any[]).findIndex((i: any) => i[this.dataKey] === item[this.dataKey]) === index
      );
    }

    if (this.isFilterSelectedItems && this.options?.length && this.selectedItems?.length) {
      const key = this.isNotNamedArray === true ? 'value' : 'id';
      this.options = this.options.map((option) => ({
        ...option,
        isSelected: this.selectedItems.findIndex((selectedItem) => selectedItem?.[key] === option?.[key]) !== -1
      }));
    }

    if (fieldToRewrite === 'options') {
      if (this.multiSelect?.scroller?.elementViewChild?.nativeElement) {
        this.renderer2.setStyle(this.multiSelect.scroller.elementViewChild.nativeElement, 'height', this.scrollHeight);
      }
    }
  }

  public onHideChangeValue() {
    this.selectedItemsChange.emit(this.selectedItems);
    this.changeValue.emit(this.selectedItems);
    this.openedChange.emit(false);
    this.panelHide.emit();
  }

  public onChangeValue(event: any) {
    if (this.timerFromChange) {
      clearTimeout(this.timerFromChange);
    }
    if (this.isNotNamedArray) {
      const values = event.value.map((v) => v.value);
      this.changeValue.emit(values);
      this.selectedItemsChange.emit(values);
    } else {
      this.changeValue.emit(event.value);
      this.selectedItemsChange.emit(event.value);
    }
    if (this.isSendFromChange) {
      this.timerFromChange = setTimeout(() => {
        this.onFinalChange.emit(this.selectedItems);
      }, this.TIMER_FULL_CHANGE);
    }
    if (this.multiSelect?.filterValue?.length) {
      this.multiSelect.resetFilter();
    }
  }

  public onPanelHide() {
    if (this.multiSelect?.filterValue?.length) {
      this.multiSelect.resetFilter();
    }

    if (this.selectedItemsOptions.updateOn === 'hide') {
      this.onHideChangeValue();
    } else {
      setTimeout(() => {
        this.panelHide.emit(this.selectedItems);
      }, 0);
      this.openedChange.emit(false);
    }
  }

  public onFilterItems($event: any) {
    if (
      $event &&
      document.querySelector('.p-overlay')?.getBoundingClientRect()?.height <
        window.outerHeight - document.querySelector('p-multiSelect').getBoundingClientRect().bottom &&
      this.moveDown
    ) {
      this.renderer2.setStyle(document.querySelector('.p-overlay'), 'top', '1030px', 1);
    }
    if (
      $event &&
      document.querySelector('.p-overlay')?.getBoundingClientRect()?.height >
        window.outerHeight - document.querySelector('p-multiSelect').getBoundingClientRect().bottom &&
      this.moveDown
    ) {
      this.renderer2.setStyle(document.querySelector('.p-overlay'), 'top', '647px', 1);
    }
    this.onFilter.emit($event);
  }

  public onClear() {
    this.changeValue.emit([]);
    this.selectedItemsChange.emit([]);
  }
}
